import { ChevronDown, ChevronLeft, Clock, LayoutGrid, Star, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

// Type-only import — erased at compile time, so no runtime cycle with AppLayout.
import type { NavItem, NavSection } from '../../app/AppLayout.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../components/shadcn/index.js';

/**
 * "Menus" — the titlebar app launcher.
 *
 * Two levels instead of the old eighty-links-at-once wall: the panel opens on a
 * grid of big section tiles (plus the user's pinned pages and their recent
 * pages), and clicking a tile drills into that one section's links. A star on
 * every link pins it to the top of the launcher.
 *
 * Pins and recents are localStorage, per browser — they are a hand's muscle
 * memory, not shop data, so they don't belong on the server. Both are stored
 * as route paths and resolved against the CURRENT nav on every open, so a page
 * the user lost permission to simply stops appearing instead of dead-linking.
 *
 * The dropdown is CONTROLLED and its children are plain links/buttons rather
 * than Radix menu items: a tile press and a star press must NOT close the
 * panel, and Radix closes on any item select. Esc / outside-click / positioning
 * stay Radix's job.
 */

const PINS_KEY = 'erp-menu-pins';
const RECENTS_KEY = 'erp-menu-recents';
const RECENTS_MAX = 8;

function readList(key: string): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Record the page a route belongs to, for the launcher's "Recent" row.
 * Longest-prefix match, so /purchasing/bills/123 counts as a visit to Bills —
 * the OPENABLE page is what "recent" means, not the individual document.
 */
export function recordMenuVisit(sections: NavSection[], pathname: string): void {
  const hit = sections
    .flatMap((s) => s.items)
    .map((i) => i.to)
    .filter((to) => to !== '' && (pathname === to || pathname.startsWith(`${to}/`)))
    .reduce((best, to) => (to.length > best.length ? to : best), '');
  if (!hit) return;
  const prev = readList(RECENTS_KEY);
  if (prev[0] === hit) return;
  localStorage.setItem(RECENTS_KEY, JSON.stringify([hit, ...prev.filter((to) => to !== hit)].slice(0, RECENTS_MAX)));
}

export function TopNavMenu({ sections }: { sections: NavSection[] }): JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // null = the tile grid; a section labelKey = drilled into that section.
  const [view, setView] = useState<string | null>(null);
  const [pins, setPins] = useState<string[]>(() => readList(PINS_KEY));

  const byTo = useMemo(() => {
    const m = new Map<string, NavItem>();
    for (const s of sections) for (const it of s.items) if (it.to && !m.has(it.to)) m.set(it.to, it);
    return m;
  }, [sections]);

  // Same active rule as the sidebar: longest matching prefix wins.
  const activePath = sections
    .flatMap((s) => s.items)
    .map((i) => i.to)
    .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .reduce((best, to) => (to.length > best.length ? to : best), '');

  // Read on every open (cheap), resolved against today's nav — a pinned page
  // whose permission went away vanishes instead of 403ing.
  const pinned = pins.filter((to) => byTo.has(to));
  const recents = open ? readList(RECENTS_KEY).filter((to) => byTo.has(to) && !pinned.includes(to)) : [];

  const togglePin = (to: string): void => {
    setPins((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to];
      localStorage.setItem(PINS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const close = (): void => setOpen(false);
  const section = view != null ? sections.find((s) => s.labelKey === view) : undefined;
  const label = t('nav.quickMenu', { defaultValue: 'Menus' });
  const sectionName = (s: NavSection): string => t(s.labelKey, { defaultValue: s.labelKey.split('.').pop() });

  const chip = (to: string, unpinnable: boolean): JSX.Element => {
    const item = byTo.get(to)!;
    const Icon = item.icon;
    return (
      <span key={to} className="group relative">
        <Link
          to={to}
          onClick={close}
          className="flex items-center gap-1.5 rounded-full border border-border bg-bg-subtle px-2.5 py-1.5 text-xs font-medium transition-[transform,background-color,color] duration-150 ease-out hover:bg-brand/10 hover:text-brand active:scale-[0.97] motion-reduce:transition-none"
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="max-w-[9rem] truncate">{t(item.labelKey)}</span>
        </Link>
        {unpinnable && (
          <button
            type="button"
            onClick={() => togglePin(to)}
            title={t('nav.menuUnpin', { defaultValue: 'Unpin' })}
            aria-label={t('nav.menuUnpin', { defaultValue: 'Unpin' })}
            className="absolute -end-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full border border-border bg-bg text-fg-muted shadow-sm hover:text-danger group-hover:flex"
          >
            <X className="h-2.5 w-2.5" aria-hidden="true" />
          </button>
        )}
      </span>
    );
  };

  return (
    // The wrapper carries the responsive hiding: `.erp-shell .icon-btn` (0,2,0)
    // out-specifies single-class Tailwind utilities, so `hidden` directly on
    // the button would lose. On a plain span there is no shell rule to fight.
    <span className="hidden md:block">
      <DropdownMenu
        // Radix defaults to ltr inside its portal (no app-wide DirectionProvider).
        dir={i18n.dir() === 'rtl' ? 'rtl' : 'ltr'}
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          // Land on the tile grid on OPEN (resetting on close would flash the
          // grid under the closing animation).
          if (o) setView(null);
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="icon-btn"
            style={{ width: 'auto', paddingInline: 8, gap: 4 }}
            aria-label={label}
            title={label}
          >
            <LayoutGrid aria-hidden="true" />
            <span style={{ fontWeight: 700, fontSize: 12 }}>{label}</span>
            {/* No size utilities: `.erp-shell .icon-btn svg` (shell.css) out-specifies
                them and fixes the icon at 18px anyway. */}
            <ChevronDown className="opacity-70" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="w-[min(96vw,640px)] max-h-[min(80vh,600px)] overflow-y-auto p-3"
        >
          {!section ? (
            <div key="home" className="flex flex-col gap-4 duration-150 animate-in fade-in-0 motion-reduce:animate-none">
              {pinned.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                    <Star className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('nav.menuPinned', { defaultValue: 'Pinned' })}
                  </div>
                  <div className="flex flex-wrap gap-1.5">{pinned.map((to) => chip(to, true))}</div>
                </div>
              )}
              {recents.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('nav.menuRecent', { defaultValue: 'Recent' })}
                  </div>
                  <div className="flex flex-wrap gap-1.5">{recents.map((to) => chip(to, false))}</div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 md:grid-cols-5">
                {sections.map((s) => {
                  const SectionIcon = s.icon ?? LayoutGrid;
                  return (
                    <button
                      key={s.labelKey}
                      type="button"
                      onClick={() => setView(s.labelKey)}
                      className="flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-[transform,background-color] duration-150 ease-out hover:bg-bg-subtle active:scale-[0.97] motion-reduce:transition-none"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                        <SectionIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="w-full truncate text-xs font-medium">{sectionName(s)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div key={section.labelKey} className="duration-150 animate-in fade-in-0 motion-reduce:animate-none">
              <button
                type="button"
                onClick={() => setView(null)}
                className="mb-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-semibold transition-colors hover:bg-bg-subtle"
              >
                <ChevronLeft className="h-4 w-4 text-fg-muted rtl:rotate-180" aria-hidden="true" />
                {section.icon && <section.icon className="h-4 w-4 text-brand" aria-hidden="true" />}
                {sectionName(section)}
                <span className="ms-1 text-xs font-normal text-fg-subtle">
                  {t('nav.menuBack', { defaultValue: 'All menus' })}
                </span>
              </button>
              <div className="columns-2 gap-4">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = item.to !== '' && item.to === activePath;
                  const isPinned = pins.includes(item.to);
                  return (
                    <div
                      key={item.to}
                      className="group flex break-inside-avoid items-center rounded-md transition-colors hover:bg-bg-subtle"
                    >
                      <Link
                        to={item.to}
                        onClick={close}
                        aria-current={active ? 'page' : undefined}
                        className={`flex min-w-0 grow items-center gap-2 rounded-md px-2 py-1.5 text-sm${active ? ' font-semibold text-brand' : ''}`}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => togglePin(item.to)}
                        title={t(isPinned ? 'nav.menuUnpin' : 'nav.menuPin', { defaultValue: isPinned ? 'Unpin' : 'Pin' })}
                        aria-label={t(isPinned ? 'nav.menuUnpin' : 'nav.menuPin', { defaultValue: isPinned ? 'Unpin' : 'Pin' })}
                        aria-pressed={isPinned}
                        className={`me-1 rounded p-1 transition-opacity focus-visible:opacity-100 ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      >
                        <Star
                          className={isPinned ? 'h-3.5 w-3.5 fill-warning text-warning' : 'h-3.5 w-3.5 text-fg-subtle hover:text-fg'}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
